import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@draftedge/ui';
import { track } from '@/lib/analytics';
import { useAuthStore } from '@/lib/auth';
import { initialsOf } from '@/lib/initials';
import { BrandLogo } from '@/components/BrandLogo';

/**
 * Global, auth-aware top bar. Single source of truth for "are you signed in"
 * affordances so no page disagrees:
 *   - signed in  → Dashboard link + avatar (→ account) + Logout
 *   - signed out → Sign in + Get started
 *
 * Mounted once in App.tsx INSIDE the BootstrapGate, so `isBootstrapping` is
 * already resolved here — the header never flashes the wrong state. (We still
 * guard defensively.)
 *
 * Hidden on the focused draft surfaces (/drafts/new, /drafts/:id) which run
 * their own full-screen chrome.
 */

/*
 * ONE MENU, AT EVERY WIDTH (owner 2026-08-06).
 *
 * There is no inline top-level nav. The bar carries the logo, the hamburger,
 * and the auth cluster — nothing else. Previous passes tried inline links at
 * lg/2xl and a scrolling strip below, and both kept colliding with the auth
 * buttons as links were added; the strip also buried whatever sat past the
 * right edge. A single drawer is width-independent, so adding a tool later
 * cannot re-break the bar.
 *
 * Grouping is preserved INSIDE the drawer: draft tools are what people pay
 * for (three of the four are tab-bar roots in the native app), research is
 * free browsing.
 */
const DRAFT_LINKS: Array<{ to: string; label: string }> = [
  { to: '/drafts', label: 'Drafts' },
  { to: '/cheatsheet', label: 'Cheat Sheets' },
  { to: '/mocks', label: 'Mock Drafts' },
  { to: '/compare', label: 'Compare Players' },
];

const RESEARCH_LINKS: Array<{ to: string; label: string }> = [
  { to: '/rankings', label: 'Rankings' },
  { to: '/rookies', label: 'Rookies' },
  { to: '/trending', label: 'Waiver Wire' },
  { to: '/exposure', label: 'Exposure' },
  { to: '/depth-charts', label: 'Depth Charts' },
  { to: '/injuries', label: 'Injuries' },
  { to: '/news', label: 'News' },
];

export function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const [menuOpen, setMenuOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Navigating away must close the drawer, or it stays open over the new page.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Escape closes it; a menu you can only dismiss by clicking exactly the right
  // spot is a trap on a phone.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // Keep the draft room / new-draft wizard distraction-free.
  if (/^\/drafts\/.+/.test(location.pathname)) {
    return null;
  }

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout(); // optimistic — clears auth state synchronously first
    navigate('/', { replace: true });
  };

  const drawerLink = (to: string, label: string, strong = false) => {
    const active =
      location.pathname === to || location.pathname.startsWith(`${to}/`);
    return (
      <Link
        key={to}
        to={to}
        aria-current={active ? 'page' : undefined}
        onClick={() => setMenuOpen(false)}
        className={`block rounded-lg px-3 py-2.5 text-sm transition-colors ${
          active
            ? 'bg-line/40 font-semibold text-ink'
            : 'text-ink-muted hover:bg-line/25 hover:text-ink'
        } ${strong && !active ? 'font-semibold text-ink' : ''}`}
      >
        {label}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-40 border-b border-line/50 bg-surface/95 backdrop-blur supports-[backdrop-filter]:bg-surface/80">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          {/* Hamburger. Present at every width — this is the only nav. */}
          <button
            type="button"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="site-menu"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 flex-none items-center justify-center rounded-lg text-ink transition-colors hover:bg-line/30"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
            >
              {menuOpen ? (
                <>
                  <path d="M6 6l12 12" />
                  <path d="M18 6L6 18" />
                </>
              ) : (
                <>
                  <path d="M4 7h16" />
                  <path d="M4 12h16" />
                  <path d="M4 17h16" />
                </>
              )}
            </svg>
          </button>
          <Link to="/" aria-label="DraftEdge home" className="flex flex-none items-center">
            <BrandLogo className="h-6" />
          </Link>
        </div>

        {/* Right cluster — never rendered until bootstrap settles. */}
        {isBootstrapping ? (
          <div className="h-9 w-24" aria-hidden="true" />
        ) : isAuthenticated ? (
          <nav className="flex flex-none items-center gap-2 sm:gap-3">
            <Link to="/dashboard" className="hidden sm:block">
              <Button variant="ghost" size="sm">
                Dashboard
              </Button>
            </Link>
            <Link
              to="/account"
              title="Account settings"
              className="flex h-9 w-9 items-center justify-center rounded-pill bg-brand-primary text-xs font-semibold uppercase text-white transition hover:opacity-90"
            >
              {initialsOf(user?.first_name, user?.last_name, user?.email)}
            </Link>
            <Button variant="secondary" size="sm" onClick={handleLogout}>
              Logout
            </Button>
          </nav>
        ) : (
          <nav className="flex flex-none items-center gap-2 sm:gap-3">
            <Link to="/login">
              <Button variant="ghost" size="sm">
                Sign in
              </Button>
            </Link>
            <Link
              to="/register"
              onClick={() => track('cta_click', { cta_location: 'header' })}
            >
              <Button variant="primary" size="sm">
                Get started
              </Button>
            </Link>
          </nav>
        )}
      </div>

      {menuOpen && (
        <>
          {/* Backdrop. Sits under the panel but over the page so any outside
              click dismisses, which is what people expect from a drawer. */}
          <div
            className="fixed inset-0 top-[57px] z-30 bg-black/40"
            aria-hidden="true"
            onClick={() => setMenuOpen(false)}
          />
          <div
            id="site-menu"
            ref={panelRef}
            className="absolute inset-x-0 top-full z-40 max-h-[calc(100vh-57px)] overflow-y-auto border-b border-line/50 bg-surface shadow-xl"
          >
            <nav
              aria-label="Site menu"
              className="mx-auto max-w-6xl px-4 pb-4 pt-3 sm:px-6"
            >
              <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
                Draft tools
              </p>
              {DRAFT_LINKS.map((l) => drawerLink(l.to, l.label, true))}

              <p className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
                Research
              </p>
              {RESEARCH_LINKS.map((l) => drawerLink(l.to, l.label))}

              {/* Auth repeated below a separator: on a phone the top-bar
                  buttons are small targets, and Dashboard is hidden there. */}
              <div className="mt-3 border-t border-line/50 pt-3">
                {isAuthenticated ? (
                  <>
                    {drawerLink('/dashboard', 'Dashboard', true)}
                    {drawerLink('/account', 'Account')}
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="block w-full rounded-lg px-3 py-2.5 text-left text-sm text-ink-muted transition-colors hover:bg-line/25 hover:text-ink"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    {drawerLink('/login', 'Sign in')}
                    <Link
                      to="/register"
                      onClick={() => {
                        track('cta_click', { cta_location: 'header_menu' });
                        setMenuOpen(false);
                      }}
                      className="mt-1 block"
                    >
                      <Button variant="primary" size="sm" className="w-full">
                        Get started
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        </>
      )}
    </header>
  );
}
